// Shared delta-stat chips for the revision-diff strips. Both MeshDiff
// (vert/tri/material counts) and the texture-stats strip render a row of
// "label  value  ±delta" chips; this is the one implementation behind both.

import { Badge } from '@/components/ui/badge';
import { Tooltip } from '@/components/ui/tooltip';

/**
 * Numeric stat: shows the right-rev value (falling back to left when the
 * right rev lacks it) plus a signed delta badge when both revs have a
 * number. Green = grew, red = shrank, neutral = unchanged/unknown.
 */
export function NumStat({
  label,
  left,
  right,
}: {
  label: string;
  left: number | undefined;
  right: number | undefined;
}) {
  const both = typeof left === 'number' && typeof right === 'number';
  const delta = both ? (right as number) - (left as number) : null;
  const tone: 'green' | 'red' | 'neutral' =
    delta === null ? 'neutral' : delta > 0 ? 'green' : delta < 0 ? 'red' : 'neutral';
  const deltaLabel =
    delta === null ? '—' : delta === 0 ? '±0' : (delta > 0 ? '+' : '') + delta.toLocaleString();
  return (
    <Tooltip label={`${label}: ${left ?? '?'} → ${right ?? '?'}`}>
      <div className="flex cursor-help items-center gap-1">
        <span className="text-xs text-muted-foreground">{label}</span>
        <span className="text-xs">{(right ?? left ?? 0).toLocaleString()}</span>
        {both && <Badge tone={tone}>{deltaLabel}</Badge>}
      </div>
    </Tooltip>
  );
}

/**
 * String stat: shows the right-rev value and, when it differs from the
 * left rev, a yellow "changed" badge (matching the JSON diff's changed
 * color). Used for resolution (`1024×2048`), pixel format, LOD group, etc.
 */
export function TextStat({
  label,
  left,
  right,
}: {
  label: string;
  left: string | undefined | null;
  right: string | undefined | null;
}) {
  const l = left ?? null;
  const r = right ?? null;
  const both = l !== null && r !== null;
  const changed = both && l !== r;
  const shown = r ?? l ?? '—';
  return (
    <Tooltip label={changed ? `${label}: ${l} → ${r}` : `${label}: ${shown}`}>
      <div className="flex cursor-help items-center gap-1">
        <span className="text-xs text-muted-foreground">{label}</span>
        <span className="text-xs">{shown}</span>
        {changed && <Badge tone="yellow">changed</Badge>}
      </div>
    </Tooltip>
  );
}
